<template>
    <div class="mb-3">
        <!-- showing this when the step is 'readonly' and presented as summarized info -->
        <div v-if="!!this.isStepReadOnly && (!fieldData.hasOwnProperty('allowReadOnly') || !!fieldData.allowReadOnly) && fieldData.type != 'fulladdress' ">
            <div v-if="!fieldData.hasOwnProperty('hideWhenReadOnly') || !fieldData.hideWhenReadOnly">
                <span v-show="!!fieldData.label">&#x2022; {{ this.getTranslatedText(fieldData.label, 'fields.labels') ?? '' }}</span>
                <p v-if="this.app[fieldData.name]" class="text-muted" v-bind="fieldData.attrs">{{this.app[fieldData.name] ?? ''}}</p>
                <p v-else class="text-danger"><i>{{ this.getTranslatedText('__answer_not_provided__', 'fields.labels') }}</i></p>
            </div>
        </div>
        <!-- regular input group -->
        <div v-else>
            <label v-show="!!fieldData.label && fieldData.label.length > 0" :for="fieldData.name" class="form-label" v-html="fieldLabel"></label>
            <div class="mb-3" :class="!!fieldData.prependInputSpan ? 'input-group' : ''">
                <span v-if="!!fieldData.prependInputSpan" class="input-group-text">{{fieldData.prependInputSpan}}</span>
                <component :is="this.getFieldComponent(fieldData.type)"
                    :showAs="fieldData.as"
                    :id="fieldData.name"
                    :name="fieldData.name"
                    :addressLabel="this.getTranslatedText(fieldData.addressLabel, 'fields.labels') ?? ''"
                    :type="fieldData.type ?? 'text'"
                    :fieldInnerClass="fieldData.fieldInnerClass ?? null"
                    v-bind="fieldData.attrs"
                    v-model="this.app[fieldData.name]"
                    :value="this.app[fieldData.name] ?? null"
                    :placeholder="this.getTranslatedText(fieldData.placeholder, 'fields.placeholders') ?? ''"
                    :choices="this.getDynamicChoices(fieldData.choices ?? null)"
                    :isMultipleOption="fieldData.isMultipleOption ?? false"
                    :lang="!!this.config.lang && this.config.lang == 'fr' ? 'fr' : 'en'"
                    @input="updateFieldValue($event,true); debounceHandler($event);"
                    @inputFromRadio="handleInputFromRadio($event)"
                    @inputFromRadioButton="handleInputFromRadioButton($event)"
                    @goToNextStep="$emit('goToNextStep');"
                    @validateAddress="this.validateAddressField"
                    @blur="this.validateField($event.target.value)"
                    @blurFromDate="this.validateField"
                    :fieldValidator="this.fieldValidator"
                    :inputColor="this.inputColor"
                    :isStepReadOnly="!!this.isStepReadOnly && (!fieldData.hasOwnProperty('allowReadOnly') || !!fieldData.allowReadOnly)"
                    :hideSubtitles="!!fieldData.hideSubtitles"
                    :prepopAddress="this.app['prepop_address'] ?? null"
                    :addressProp="this.app['address'] ?? null"
                    :productName="this.app['product_name'] ?? null"
                    ></component>
                <span v-if="!!fieldData.appendInputSpan" class="input-group-text">{{fieldData.appendInputSpan}}</span>
            </div>
        </div>
        <div v-if="!this.isValidField && this.shouldValidate" class="text-danger">
            <LcFieldErrors :errors="this.fieldValidator ? this.fieldValidator.errorMessage : null" />
        </div>
    </div>
</template>

<script>

import * as fieldHelper from "@/form/helpers/field-types.js";
import { inject } from 'vue';
import {fieldValidationHelper, addressFieldValidationHelper } from "@/form/helpers/field-validations.js";
import LcFieldErrors from '@/components/form/LcFieldErrors.vue';
import debounce from 'lodash.debounce';

export default {
    name: 'LcField',
    emits: ["goToNextStep"],
    setup(props) {
        const { app, config, updateApplication, updateApplicationField, getTranslatedText } = inject('app'); // app is being provided in Application.vue
        return {
            app,
            config,
            updateApplication,
            updateApplicationField,
            getTranslatedText
        }
    },
    components: {
        LcFieldErrors
    },
    props: {
        fieldData: {
            type: Object,
            required: true,
        },
        shouldValidate: {
            type: Boolean,
            default: true
        },
        fieldValidator: {
            type: Object,
            default: {}
        },
        inputColor: {
            type: Object,
            default: {}
        },
        isStepReadOnly: {
            type: Boolean,
            default: false
        },
        isStepEditable: {
            type: Boolean,
            default: true
        },
        // ...fieldHelper.getComponents()
    },
    data: () => {
        return {}
    },
    methods: {
        updateFieldValue(value, shouldSkipValidation = false) {
            let trimmedValue = (typeof value === 'string') ? value.trim() : value;
            this.fieldData.value = trimmedValue;
            this.updateApplicationField(this.fieldData.name, trimmedValue);
            if( !shouldSkipValidation ) {
                this.validateField(trimmedValue);
            }
        },
        handleInputFromRadioButton(value) {
            let skipValidation = true;
            this.updateFieldValue(value, skipValidation);
            this.$emit('goToNextStep', skipValidation);
        },
        handleInputFromRadio(value) {
            this.updateFieldValue(value);
        },
        getFieldComponent(fieldType) {
            return fieldHelper.getComponent(fieldType);
        },
        validateField(value) {
            // console.log('value');
            // console.log(value);
            this.fieldValidator.value = value;
            let validatorHelper = fieldValidationHelper(this.fieldValidator, this.app);
            // console.log('validatorHelper');
            // console.log(validatorHelper);
            this.fieldValidator.isValid = validatorHelper.isValid;
            this.fieldValidator.errorMessage = validatorHelper.messages;

            if(!!validatorHelper.isValid) {
                var emailFieldsToValidate = ['email'];
                if(emailFieldsToValidate.includes(this.fieldData.name)) {
                    this.validateEmailWithLC(value);
                }
            }
        },
        validateAddressField(addressValue) {
            // console.log('addressValue');
            // console.log(addressValue);
            this.fieldValidator.value = addressValue;
            let validatorHelper = addressFieldValidationHelper(addressValue);
            // console.log('validatorHelper');
            // console.log(validatorHelper);
            this.fieldValidator.isValid = validatorHelper.isValid;
            this.fieldValidator.invalidAddressFields = validatorHelper.invalidFields ?? [];
        },
        async validateEmailWithLC(value) {

            var email = value;
            var knownGoodDomains = ["loanscanada.ca","gmail.com","hotmail.com","outlook.com","yahoo.com","icloud.com","yahoo.ca","live.ca","hotmail.ca","live.com","msn.com","shaw.ca","rogers.com","hotmail.fr","outlook.fr","telus.net",];
            // var validExtensions = ["com","ca","fr",];

            var emailParts = email.split('@');
            var domain = emailParts[1];
            // var tld = domain.split('.').pop();

            if (
                knownGoodDomains.includes(domain)
                // && validExtensions.includes(tld)
            ) {
                return true
            }

            var screeningEmailResponse = await
                axios.post('/portal/member/email-validation', {
                    email: value,
                })
                .then(res => {
                    return !!res.data.status;
                }).catch(err => {
                    return true;
                });

            if(screeningEmailResponse == true) {
                this.fieldValidator.isValid = true;
                this.fieldValidator.errorMessage = [];
            }
            else {
                this.fieldValidator.isValid = false;
                this.fieldValidator.errorMessage.push('Your email appears to be invalid.');
            }
        },
        getDynamicChoices(choices = null) {
            if(!!choices && choices.length > 0) {
                return typeof choices == 'function' ? choices({ ...this.app }) : choices;
            }
            return false;
        }
    },
    computed: {
        isValidField() {
            return this.fieldValidator && !!this.fieldValidator.isValid;
        },
        fieldLabel() {
            let label = this.fieldData.label;
            let labelKey = typeof this.fieldData.label == 'function' ? this.fieldData.label({ ...this.app }) : this.fieldData.label;
            return this.getTranslatedText(labelKey, 'fields.labels') ?? '';
        }
    },
    created() {
        this.debounceHandler = debounce(value => {
            this.updateFieldValue(value);
        }, 500);
    },
    beforeUnmount() {
        this.debounceHandler.cancel();
    },
    mounted() {
        // console.log('LcField component mounted');
    },
    watch: {
    }

};
</script>
